// THE SHARED, VALIDATED SERVER QUOTE — one question, asked once, answered to every consumer of the same cart.
//
// Three surfaces price the same cart: the drawer and the cart page (both the design, via DesignSurface's CartQuoteBridge) and the
// checkout card panel (startDesignQuoteWatch). Each used to ask the server on every refresh/remount/navigation, so a cart that had not
// changed still showed "confirming the price" and re-fetched, and two consumers raced two answers for one cart. This is the ONE
// place that keys a quote by exactly what the server was asked, shares an in-flight request, and remembers a settled success.
//
// What it is NOT: a second cart, a price source or a validator. The money still comes only from `quoteDesignOrder` (which refuses
// anything but a validated, consistent backend answer); this holds that answer. Therefore:
//   * the key is the FULL server request (mountain, every transport leg, every product line, dates) plus the plan fingerprint plus a
//     caller scope — a changed quantity, fare, date, product mapping, tip or member/guest scope is a different key, never a reuse;
//   * only a SUCCESS is remembered, and only briefly (ttl): a failure, a refusal or a pending answer is never replayed;
//   * the store is tiny and per-document (it dies with the page): no persistence, no cross-visitor sharing, no global mutation hook;
//   * a late answer never overwrites a newer one — each caller applies its own currency check to what it receives, and a result is
//     only remembered under the key it was asked for.

/** Stable, order-independent text for a JSON-able value (object keys sorted) — equal inputs always give equal keys. */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v) ?? "null";
}

/** The identity of a quote question. `request` is exactly what is POSTed (never the client's own estimate); `scope` is the
 * non-secret pricing context the server may also read (e.g. member vs guest). */
export function designQuoteKey(parts: { request: unknown; fingerprint: string; scope?: string }): string {
  return stable({ request: parts.request, fingerprint: parts.fingerprint, scope: parts.scope ?? "" });
}

/**
 * A NON-SECRET pricing identity for the signed-in state, so a quote asked under one session is never shown or reused under another.
 * The server prices customer vs guest from the visitor's own session, which this code never reads or inspects; it only sees the app's
 * session token string (from the normal source session state) to notice that it CHANGED. It keeps the last token string it was handed in
 * this closure's private memory (the same string the app's session store already holds in memory) and compares by strict equality, so
 * two different tokens can never look the same. That value is never returned, never part of a cache key, and never logged or stored:
 * the only output is `guest` while signed out, or `customer:<n>` while signed in, where n increments each time the signed-in token
 * changes (login, logout→login, a different customer), so even an identical cart under a new session has a new context.
 */
export function createSessionContext(): (token: string | null | undefined) => string {
  let last: string | null = null;
  let generation = 0;
  return (token) => {
    if (!token) {
      last = null; // a logout is a change: the next login is a new generation even for the same customer
      return "guest";
    }
    if (token !== last) {
      last = token;
      generation++;
    }
    return `customer:${generation}`;
  };
}

export interface QuoteShare<T> {
  /** A fresh remembered success for this exact key, or null. */
  peek: (key: string, now?: number) => T | null;
  /** The answer for this key: the remembered success, or the request already in flight, or a new one. */
  run: (key: string, ask: () => Promise<T>, opts?: { accept?: (r: T) => boolean; now?: () => number }) => Promise<T>;
  clear: () => void;
}

export function createQuoteShare<T>({ ttlMs = 15_000, max = 8 }: { ttlMs?: number; max?: number } = {}): QuoteShare<T> {
  const settled = new Map<string, { at: number; value: T }>();
  const inflight = new Map<string, Promise<T>>();
  return {
    peek(key, now = Date.now()) {
      const hit = settled.get(key);
      if (!hit) return null;
      if (now - hit.at > ttlMs) {
        settled.delete(key);
        return null;
      }
      return hit.value;
    },
    run(key, ask, opts = {}) {
      const clock = opts.now ?? Date.now;
      const hit = this.peek(key, clock());
      if (hit) return Promise.resolve(hit);
      const running = inflight.get(key);
      if (running) return running;
      const p = (async () => {
        try {
          const value = await ask();
          // Remember only an accepted success, under the key it was asked for. A failure/refusal/pending is returned to the callers
          // that asked and forgotten, so the next ask is a real one.
          if (opts.accept?.(value)) {
            settled.set(key, { at: clock(), value });
            while (settled.size > max) {
              const oldest = settled.keys().next(); // Map iterates in insertion order: the first key is the oldest
              if (oldest.done) break;
              settled.delete(oldest.value);
            }
          }
          return value;
        } finally {
          inflight.delete(key); // a rejection or an interrupted request leaves no shared promise behind
        }
      })();
      inflight.set(key, p);
      return p;
    },
    clear() {
      settled.clear();
      inflight.clear();
    },
  };
}
